/**
 * Intégration `history.pushState` pour la navigation sub-view.
 *
 * Pourquoi : permet au bouton retour hardware (rare en PWA standalone iOS,
 * mais existe sur trackpad macOS Safari + Android) ET au geste swipe-back
 * de partager le même mécanisme de fond. La pile history est notre source
 * de vérité, les sub-views suivent.
 *
 * Modèle :
 *   - Au boot, on fait un `replaceState({view: "inbox"})` baseline.
 *   - Entrer un thread/tracker fait `pushState({view, ...args})`.
 *   - Sortir d'un sub-view fait `history.back()` qui déclenche `popstate`.
 *   - L'écouteur `popstate` reset l'état UI au state précédent.
 *
 * Garde-fous :
 *   - Si on tape `goBack()` côté UI sans history (e.g. cold-start direct
 *     dans un thread via deep-link notif), `history.back()` peut tenter
 *     de quitter la PWA. On gère via une marque "depth" interne.
 */

interface NavState {
  /**
   * `tab` = une racine d'onglet (elles vivent toutes au même étage : passer
   * d'un onglet à l'autre REMPLACE l'entrée, sinon « retour » remonterait
   * l'historique des onglets au lieu de sortir de l'app).
   * `thread` et `crm` sont les sous-vues, elles empilent.
   */
  view: "tab" | "thread" | "crm";
  /** Onglet actif quand `view === "tab"`. */
  tab?: string;
  threadId?: string;
  /** Fiche société ouverte dans l'onglet Sociétés. Absent = la liste. */
  companyUid?: string;
}

let depth = 0; // nombre de pushes qu'on a faits depuis le baseline

/** À appeler une fois au boot, après le routing initial des deep-links. */
export function initHistoryBaseline(tab = "inbox"): void {
  // Replace l'entrée courante avec un state explicite. Ne crée pas de
  // nouvelle entrée — overwrite la position actuelle de la pile.
  history.replaceState(
    { view: "tab", tab, _depth: 0 } satisfies NavState & { _depth: number },
    "",
  );
  depth = 0;
}

/** Push un nouvel état. Appelé quand on entre dans un sub-view. */
export function pushNav(state: NavState): void {
  depth += 1;
  history.pushState({ ...state, _depth: depth }, "");
}

/**
 * Remplace l'entrée courante SANS empiler.
 *
 * Sert au chaînage d'un même écran : passer d'une fiche société à la
 * suivante ne doit pas laisser une entrée derrière soi, sinon sortir après
 * vingt fiches demanderait vingt retours et il devient impossible d'arrêter.
 * `depth` ne bouge pas — on reste au même étage de la pile.
 */
export function replaceNav(state: NavState): void {
  history.replaceState({ ...state, _depth: depth }, "");
}

/**
 * Sort du sub-view courant via `history.back()`. Si la pile interne est
 * vide (depth=0), no-op : protège contre une fermeture intempestive de
 * la PWA quand `goBack()` est appelé sans push préalable.
 */
export function popNav(): void {
  if (depth <= 0) return;
  history.back(); // déclenche popstate, qui décrémente depth
}

/**
 * Branche l'écouteur `popstate`. Le callback reçoit le state cible
 * (= ce qu'il y avait dans le `pushState` précédent, ou le baseline).
 * Charge à l'appelant de mettre à jour l'UI en conséquence.
 */
export function onPopNav(handler: (state: NavState | null) => void): () => void {
  function listener(e: PopStateEvent) {
    const s = (e.state as (NavState & { _depth?: number }) | null) ?? null;
    depth = s?._depth ?? 0;
    handler(s);
  }
  window.addEventListener("popstate", listener);
  return () => window.removeEventListener("popstate", listener);
}
